import React from 'react';
import { MockedProvider, MockedProviderProps, MockedResponse } from '@apollo/client/testing';
import { QueryParamProvider } from 'use-query-params';
import { MemoryRouter } from 'react-router-dom';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { render, renderHook, RenderResult, screen, waitForElementToBeRemoved } from '@testing-library/react';
import { Provider } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import rootReducer from '../store/rootReducer';

const createTestStore = () =>
  configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: ['salesWizard/onClick'],
        },
      }),
  });

const routerComponentWrapper = (component: React.ReactNode, initialRoute: string): JSX.Element => {
  return (
    <MemoryRouter initialEntries={[initialRoute]}>
      <QueryParamProvider adapter={ReactRouter6Adapter}>{component}</QueryParamProvider>
    </MemoryRouter>
  );
};

const appRerenderWrapper = (
  component: React.ReactNode,
  mockConfig?: MockedProviderProps,
  initialRoute = '/'
): JSX.Element => {
  const store = createTestStore();
  return (
    <Provider store={store}>
      <MockedProvider {...(mockConfig ?? {})}>{routerComponentWrapper(component, initialRoute)}</MockedProvider>
    </Provider>
  );
};

interface ICustomRenderResult extends RenderResult {}

/**
 * Main helper test util wrapper used to test bigger component where  graphql are required
 * @param ui
 * @param mockConfig
 * @param initialRoute
 * @param renderMethod
 * @returns
 */
const appTestWrapperWithConfig = (
  ui: React.ReactNode,
  mockConfig?: MockedProviderProps,
  initialRoute = '/',
  renderMethod: Function = render
): ICustomRenderResult => {
  const store = createTestStore();
  return {
    ...renderMethod(
      <Provider store={store}>
        <MockedProvider {...(mockConfig ?? {})}>
          <MemoryRouter initialEntries={[initialRoute]}>
            <QueryParamProvider adapter={ReactRouter6Adapter}>{ui}</QueryParamProvider>
          </MemoryRouter>
        </MockedProvider>
      </Provider>
    ),
  };
};

// When the screen is being in loading state due to any api call or side-effect this helper would be used in the test-case
// for removing the `act` warning.
const waitForLoadingToBeDone = (cb: Function, value = 'Tom liste'): Promise<unknown> => {
  const valRegx = new RegExp(value);
  return cb(valRegx);
};

const waitForNSec = (num: number) => {
  return new Promise((res) => setTimeout(() => res(true), num));
};

const skipArgument = (num: number) => new Array(num);

const loadingHolder = async () => await waitForElementToBeRemoved(() => screen.getByLabelText('loading'));

//Use the below fn for testing custom hook
function customHookWrapper(hook: any, props: any, mocks: MockedResponse[] = []) {
  const store = createTestStore();
  const wrapper = ({ children }) => (
    <Provider store={store}>
      <MockedProvider mocks={mocks} addTypename={false}>
        {children}
      </MockedProvider>
    </Provider>
  );
  const { result, rerender, unmount } = renderHook(() => hook(props), { wrapper });
  return { result, rerender, unmount };
}

export {
  routerComponentWrapper,
  appRerenderWrapper,
  appTestWrapperWithConfig,
  waitForLoadingToBeDone,
  waitForNSec,
  skipArgument,
  loadingHolder,
  customHookWrapper,
};
